import { radius } from '@alison/ui';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import type { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';

export const GLYPH_SX = {
  width: 44,
  height: 44,
  borderRadius: `${radius.lg}px`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

export function statusChipSx(status: string) {
  return (theme: Theme) => {
    const needsAttention =
      status === 'needs-attention' || status === 'unavailable' || status === 'setup-incomplete';
    const connected = status === 'connected' || needsAttention;
    return {
      bgcolor: needsAttention
        ? theme.palette.warning.bg
        : connected
          ? theme.palette.success.bg
          : theme.palette.background.platform,
      color: needsAttention
        ? theme.palette.warning.dark
        : connected
          ? theme.palette.success.dark
          : theme.palette.text.secondary,
      border: 'none',
      height: 22,
      borderRadius: `${radius.pill}px`,
      '& .MuiChip-label': { px: 1, fontSize: 10, fontWeight: 700 },
    };
  };
}

export function StatusLabel({ status }: { status: string }): ReactNode {
  const needsAttention =
    status === 'needs-attention' || status === 'unavailable' || status === 'setup-incomplete';
  const connected = status === 'connected' || needsAttention;
  return (
    <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
      <Box
        data-testid="channel-status-dot"
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          bgcolor: needsAttention ? 'warning.main' : connected ? 'success.main' : 'text.disabled',
        }}
      />
      {statusLabel(status)}
    </Stack>
  );
}

export function statusLabel(status: string): string {
  if (status === 'connected') return 'Connected';
  if (status === 'needs-attention') return 'Needs attention';
  if (status === 'unavailable') return 'Temporarily unavailable';
  if (status === 'setup-incomplete') return 'Setup incomplete';
  return 'Not connected';
}

export function MetaRow({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <Stack direction="row" sx={{ gap: 2, fontSize: 13 }}>
      <Typography
        variant="caption"
        sx={{
          width: 100,
          flexShrink: 0,
          fontSize: 10,
          fontWeight: 700,
          textTransform: 'uppercase',
          color: 'text.secondary',
          pt: 1,
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          fontWeight: 600,
          color: 'text.primary',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        {children}
      </Box>
    </Stack>
  );
}
